import { useState, useEffect, useRef, useCallback } from 'react'
import { useN8nChat, type ChatMessage } from '@/hooks/useN8nChat'
import { useAuth } from '@/context/auth-context'
import { useChat } from '@/context/chat-context'
// Removed Conversation imports - using direct scrolling now
import { 
  Message, 
  MessageContent
} from '@/components/ai-elements/message'
import { 
  PromptInput,
  PromptInputBody,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
  PromptInputSubmit,
  PromptInputButton,
  PromptInputAttachments,
  PromptInputAttachment,
  usePromptInputAttachments,
} from '@/components/ai-elements/prompt-input'
import type { FileUIPart } from 'ai'
import { Response } from '@/components/ai-elements/response'
import { Loader } from '@/components/ai-elements/loader'
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'
import { Mic, Square, ChevronDown, ImagePlus } from 'lucide-react'
import { cn } from '@/lib/utils'

// TypeScript declarations for Web Speech API and VirtualKeyboard API
declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInstance
    webkitSpeechRecognition: new () => SpeechRecognitionInstance
  }
  
  interface VirtualKeyboard {
    overlaysContent: boolean;
    boundingRect: DOMRectReadOnly; // Use ReadOnly for compliance
    show(): void;
    hide(): void;
    addEventListener(type: 'geometrychange', listener: (event: Event & { target: VirtualKeyboard }) => void): void;
    removeEventListener(type: 'geometrychange', listener: (event: Event & { target: VirtualKeyboard }) => void): void;
  }
  
  interface Navigator {
    virtualKeyboard?: VirtualKeyboard;
  }

}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
  isFinal: boolean
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message?: string
}

interface SessionState {
  shouldAutoSend: boolean
  hasEnded: boolean
  startTime: number
  lastSpeechTime: number | null
  silenceTimer: NodeJS.Timeout | null
}

interface SpeechRecognitionInstance {
  continuous: boolean
  interimResults: boolean
  lang: string
  onstart: ((event: Event) => void) | null
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: ((event: Event) => void) | null
  start(): void
  stop(): void
  abort(): void
  maxAlternatives?: number
  _sessionState?: SessionState
  _debugId?: string
}

// Small helper component — must be rendered inside <PromptInput> so the
// attachments context is available.
function PhotoUploadButton({ disabled }: { disabled?: boolean }) {
  const attachments = usePromptInputAttachments()
  return (
    <PromptInputButton
      onClick={() => attachments.openFileDialog()}
      variant="ghost"
      title="Upload photo"
      disabled={disabled}
      className="w-10 h-10 rounded-full p-0"
    >
      <ImagePlus size={16} />
      <span className="sr-only">Upload photo</span>
    </PromptInputButton>
  )
}

interface ChatProps {
  className?: string
  placeholder?: string
  webhookUrl?: string
}

export function Chat({ 
  className, 
  placeholder = "Ask Koba, you time management assistant",
  webhookUrl
}: ChatProps) {
  const { user } = useAuth()
  
  // Extract first name from user data
  const getFirstName = () => {
    if (!user) return 'there'
    
    // Try user_metadata.full_name first (Google OAuth)
    if (user.user_metadata?.full_name) {
      const fullName = user.user_metadata.full_name as string
      return fullName.split(' ')[0]
    }
    
    // Try user_metadata.given_name (Google OAuth)
    if (user.user_metadata?.given_name) {
      return user.user_metadata.given_name as string
    }
    
    // Try email prefix as fallback
    if (user.email) {
      const emailPrefix = user.email.split('@')[0]
      return emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1)
    }
    
    return 'there'
  }
  const [isListening, setIsListening] = useState<boolean>(false)
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en-US')
  const [recognition, setRecognition] = useState<SpeechRecognitionInstance | null>(null)
  const [inputText, setInputText] = useState<string>('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  
  
  // Available languages for speech recognition
  const availableLanguages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'pt-BR', name: 'Portuguese' },
    { code: 'ru-RU', name: 'Russian' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'zh-CN', name: 'Chinese (Simplified)' },
    { code: 'ar-SA', name: 'Arabic' },
  ]
  
  // Set up VirtualKeyboard API for mobile keyboard overlay
  useEffect(() => {
    const vk = navigator.virtualKeyboard;
    
    // 1. Feature Detection
    if (!vk) {
      console.log('[Chat] VirtualKeyboard API not available.');
      return; // Stop the effect if not supported
    }

    let originalValue: boolean | undefined;

    try {
      // 2. Store original value and set the new value (only if supported)
      originalValue = vk.overlaysContent;
      vk.overlaysContent = true;
      
      console.log(`[Chat] OverlaysContent set to true. Original: ${originalValue}`);
      
      // 3. Optional: Add an event listener to re-apply if it ever reverts
      // This is a minimal safeguard without constant polling.
      const handleGeometryChange = () => {
         if (vk.overlaysContent !== true) {
             console.warn('[Chat] overlaysContent reverted, reapplying...');
             vk.overlaysContent = true;
         }
      }
      vk.addEventListener('geometrychange', handleGeometryChange);


    // 4. Cleanup Function: runs when component unmounts
      return () => {
        vk.removeEventListener('geometrychange', handleGeometryChange);
          
        // Restore to original value
        if (vk.overlaysContent !== originalValue) {
          vk.overlaysContent = originalValue ?? false; 
          console.log(`[Chat] Restored overlaysContent to ${originalValue}`);
        }
      };

    } catch (error) {
      console.error('[Chat] Failed to set virtualKeyboard.overlaysContent:', error);
      // No need to clean up if the initial set failed
    }

  }, []); // Empty deps to run only on mount/unmount

  // Initialize speech recognition
  useEffect(() => {
    const recognitionId = Math.random().toString(36).substr(2, 9)
    
    if (typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      const recognitionInstance = new SpeechRecognition()
      
      recognitionInstance.continuous = true
      recognitionInstance.interimResults = true // Enable interim results for better silence detection
      recognitionInstance.lang = selectedLanguage
      
      // Try to set shorter timeouts (browser dependent)
      if ('webkitSpeechRecognition' in window) {
        // Chrome/WebKit specific settings
        try {
          recognitionInstance.maxAlternatives = 1
        } catch {
          // Could not set maxAlternatives
        }
      }
      
      // Add debug info to instance
      recognitionInstance._debugId = recognitionId
      
      setRecognition(recognitionInstance)
      
      return () => {
        if (recognitionInstance) {
          try {
            recognitionInstance.abort() // Use abort instead of stop for cleanup
          } catch {
            // Recognition cleanup error (normal in Strict Mode)
          }
        }
      }
    }
  }, [selectedLanguage])
  
  // Auto-detect browser language (separate effect to avoid recreation)
  useEffect(() => {
    if (selectedLanguage === 'en-US') {
      const browserLang = navigator.language || 'en-US'
      const supportedLang = availableLanguages.find(lang => lang.code === browserLang)
      if (supportedLang && browserLang !== selectedLanguage) {
        setSelectedLanguage(browserLang)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount


  
  const {
    messages,
    isLoading: internalIsLoading,
    isStreaming: internalIsStreaming,
    sessionId: internalSessionId,
    sendMessage,
    clearChat: internalClearChat
  } = useN8nChat({
    webhookUrl,
    onError: () => {
      // Show generic error toast
      toast.error('Error connecting to assistant')
    }
  })

  // Use internal values directly
  const isLoading = internalIsLoading
  const isStreaming = internalIsStreaming
  const hasMessages = messages.length > 0
  const sessionId = internalSessionId

  // Get chat context for header communication
  const { 
    setLoadingState, 
    setHasMessages, 
    setConversationTitle,
    setSessionId: setContextSessionId,
    registerClearChatFunction
  } = useChat()

  // Ref for auto-scrolling to bottom
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)

  // Sync state with context
  useEffect(() => {
    setLoadingState(isLoading, isStreaming)
  }, [isLoading, isStreaming, setLoadingState])

  useEffect(() => {
    setHasMessages(hasMessages)
  }, [hasMessages, setHasMessages])

  useEffect(() => {
    setContextSessionId(sessionId)
  }, [sessionId, setContextSessionId])

  // Update conversation title - prioritize AI-generated titles, fallback to first message
  useEffect(() => {
    if (messages.length > 0) {
      // Look for the most recent message with a conversationTitle (AI-generated)
      const messageWithTitle = [...messages].reverse().find(msg => msg.conversationTitle)
      
      if (messageWithTitle?.conversationTitle) {
        // Use AI-generated title
        setConversationTitle(messageWithTitle.conversationTitle)
      } else if (messages[0]?.role === 'user') {
        // Fallback to first message as title
        const firstMessage = messages[0].content
        if (firstMessage && firstMessage.length > 0) {
          const title = firstMessage.length > 30 
            ? firstMessage.substring(0, 30) + '...'
            : firstMessage
          setConversationTitle(title)
        }
      }
    }
  }, [messages, setConversationTitle])

  // Handle clear chat - clears messages and generates new session
  const handleClearChat = useCallback(() => {
    internalClearChat()
  }, [internalClearChat])

  // Register the clear chat function with the context so the header can call it
  useEffect(() => {
    registerClearChatFunction(handleClearChat)
  }, [registerClearChatFunction, handleClearChat])

  // Simple auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isLoading, isStreaming])

  // Check if user has scrolled up from bottom
/*  const checkScrollPosition = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100 // 100px threshold
      setShowScrollToBottom(!isAtBottom && hasMessages)
    }
  }
*/
const checkScrollPosition = () => {
  // Look for the main element instead of using the ref
  const mainElement = document.querySelector('main');
  if (mainElement) {
    const { scrollTop, scrollHeight, clientHeight } = mainElement;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollToBottom(!isAtBottom && hasMessages);
  }
}
  // Add scroll listener to detect when user scrolls up
  useEffect(() => {
    const mainElement = document.querySelector('main')
    if (mainElement) {
      mainElement.addEventListener('scroll', checkScrollPosition)
      // Initial check
      checkScrollPosition()
      
      return () => {
        mainElement.removeEventListener('scroll', checkScrollPosition)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMessages])

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }


  const handleSubmit = async (message: { text?: string; files?: FileUIPart[] }, event: React.FormEvent<HTMLFormElement>) => {
    if (!message.text?.trim()) {
      return
    }
    
    // Capture photo before resetting the form (first file only)
    const photoFile = message.files?.[0]
    const photo = photoFile
      ? { url: photoFile.url!, mediaType: photoFile.mediaType!, filename: photoFile.filename }
      : undefined

    // Reset form immediately before sending to clear input
    const form = event.currentTarget
    form.reset()
    setInputText('')
    
    // Reset textarea height to original size (especially important on mobile)
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      // Force a reflow to ensure the height is properly reset
      void textareaRef.current.offsetHeight
    }
    
    await sendMessage(message.text, photo)
  }

  const startListening = () => {
    if (!recognition) {
      toast.error('Speech recognition is not supported in your browser')
      return
    }

    try {
      setIsListening(true)
      
      // Store existing text to append to later
      const existingText = inputText.trim()
      
      setInputText('Listening...')
      
      // Create a ref for auto-send state that won't be lost during re-renders
      const sessionState: {
        shouldAutoSend: boolean
        hasEnded: boolean
        startTime: number
        lastSpeechTime: number | null
        silenceTimer: NodeJS.Timeout | null
      } = { 
        shouldAutoSend: true, 
        hasEnded: false,
        startTime: Date.now(),
        lastSpeechTime: null,
        silenceTimer: null
      }
      
      recognition.onresult = (event: SpeechRecognitionEvent) => {
        if (sessionState.hasEnded) {
          return
        }
        
        const result = event.results[event.results.length - 1]
        
        // Safely extract transcript
        let transcript = ''
        if (result && result.length > 0 && result[0]) {
          transcript = result[0].transcript || ''
        }
        
        // Only process final results to avoid duplicates from interim results
        if (!result.isFinal) {
          return
        }
        
        // Record when we last received speech
        sessionState.lastSpeechTime = Date.now()
        
        // Reset silence timer on speech
        if (sessionState.silenceTimer) {
          clearTimeout(sessionState.silenceTimer)
        }
        
        // Start new silence timer (3 seconds)
        sessionState.silenceTimer = setTimeout(() => {
          if (!sessionState.hasEnded && sessionState.shouldAutoSend) {
            // Manually stop recognition and trigger auto-send
            sessionState.hasEnded = true
            sessionState.shouldAutoSend = false
            setIsListening(false)
            
            setTimeout(() => {
              const formElement = document.querySelector('form')
              if (!formElement) return
              const currentFormData = new FormData(formElement)
              const formText = currentFormData.get('message') as string || ''
              
              const finalText = formText !== 'Listening...' && formText ? formText : ''
              if (finalText && finalText.trim()) {
                sendMessage(finalText)
                setInputText('')
                // Reset textarea height after auto-sending
                if (textareaRef.current) {
                  textareaRef.current.style.height = 'auto'
                }
              }
            }, 100)
            
            recognition.stop()
          }
        }, 3000) // 3 seconds of silence
        
        // Only update if we have a valid transcript
        if (transcript && transcript.trim()) {
          setInputText(prev => {
            // If it's "Listening...", replace with existing text + new transcript
            if (prev === 'Listening...') {
              let newText = transcript.trim()
              if (existingText) {
                newText = existingText + ' ' + transcript.trim()
              }
              return newText
            }
            
            // If we already have text (not "Listening..."), append with a space
            if (prev && prev.trim()) {
              const newText = prev + ' ' + transcript.trim()
              return newText
            }
            
            // If no existing text, just use the transcript
            const newText = transcript.trim()
            return newText
          })
        }
      }

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        if (sessionState.hasEnded) {
          return
        }
        sessionState.shouldAutoSend = false
        sessionState.hasEnded = true
        setIsListening(false)
        setInputText('')
        
        if (event.error === 'not-allowed') {
          toast.error('Microphone permission denied. Please allow microphone access.')
        } else if (event.error === 'no-speech') {
          toast.error('No speech detected. Please try again.')
        } else {
          toast.error('Speech recognition error. Please try again.')
        }
      }

      recognition.onend = () => {
        if (sessionState.hasEnded) {
          return
        }
        
        // Mark session as ended to prevent duplicate processing
        sessionState.hasEnded = true
        
        // Clean up listening state
        setIsListening(false)
        
        // Only auto-send if flag is enabled (meaning it ended due to silence, not manual stop)
        if (sessionState.shouldAutoSend) {
          // Disable auto-send immediately to prevent double execution
          sessionState.shouldAutoSend = false
          
          // Get current input text at the time of ending
          setTimeout(() => {
            // Use a more direct approach to get the current text
            const formElement = document.querySelector('form')
            if (!formElement) return
            const currentFormData = new FormData(formElement)
            const formText = currentFormData.get('message') as string || ''
            
            const finalText = formText !== 'Listening...' && formText ? formText : ''
            if (finalText && finalText.trim()) {
              sendMessage(finalText)
              setInputText('') // Clear after sending
              // Reset textarea height after auto-sending
              if (textareaRef.current) {
                textareaRef.current.style.height = 'auto'
              }
            }
          }, 100) // Small delay to ensure state is updated
        }
      }

      // Expose the sessionState control to the stop function
      recognition._sessionState = sessionState

      recognition.start()
    } catch {
      toast.error('Failed to start speech recognition')
      setIsListening(false)
      setInputText('')
    }
  }

  const stopListening = () => {
    if (recognition && isListening) {
      
      // Disable auto-send by setting the session state
      if (recognition._sessionState) {
        recognition._sessionState.shouldAutoSend = false
        recognition._sessionState.hasEnded = true
      }
      
      recognition.stop()
      
      // Clean up the input text if it's problematic
      if (inputText === 'Listening...' || inputText === 'undefined' || !inputText || inputText === 'null') {
        setInputText('')
        // Reset textarea height when clearing text
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto'
        }
      }
      
      setIsListening(false)
    }
  }

  const handleLanguageChange = (newLanguage: string) => {
    if (isListening) {
      // Stop current recording but preserve any transcribed text
      stopListening()
      
      // Show user feedback
      toast.info(`Language changed to ${availableLanguages.find(l => l.code === newLanguage)?.name || newLanguage}. Recording stopped.`)
    }
    
    setSelectedLanguage(newLanguage)
  }


  const getChatStatus = () => {
    if (isLoading && !isStreaming) return 'submitted'
    if (isStreaming) return 'streaming'
    return undefined
  }


  const getLanguageCode = (langCode: string) => {
    const langMap: { [key: string]: string } = {
      'en-US': 'En',
      'en-GB': 'En',
      'es-ES': 'Es',
      'fr-FR': 'Fr',
      'de-DE': 'De',
      'it-IT': 'It',
      'pt-BR': 'Pt',
      'ru-RU': 'Ru',
      'ja-JP': 'Ja',
      'ko-KR': 'Ko',
      'zh-CN': 'Zh',
      'ar-SA': 'Ar',
    }
    return langMap[langCode] || 'En'
  }

  // Detect if device is mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           window.innerWidth <= 768
  }

  // Auto-resize textarea function (fallback for mobile)
  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'
    // Set height to scrollHeight (content height) with min-height constraint
    const newHeight = Math.max(textarea.scrollHeight, 48) // 48px = min-h-12 (3rem)
    textarea.style.height = `${newHeight}px`
  }

  // Custom key handler for mobile-friendly Enter behavior
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      // Don't submit if IME composition is in progress
      if (e.nativeEvent.isComposing) {
        return
      }

      const mobile = isMobile()
      
      if (mobile) {
        // On mobile: Enter creates new line, Shift+Enter submits
        if (e.shiftKey) {
          e.preventDefault()
          const form = e.currentTarget.form
          if (form) {
            form.requestSubmit()
          }
        } else {
          // Let Enter create new line and trigger auto-resize
          setTimeout(() => {
            if (textareaRef.current) {
              autoResizeTextarea(textareaRef.current)
            }
          }, 0)
        }
      } else {
        // On desktop: Shift+Enter creates new line, Enter submits (original behavior)
        if (e.shiftKey) {
          // Allow newline
          return
        }
        // Submit on Enter (without Shift)
        e.preventDefault()
        const form = e.currentTarget.form
        if (form) {
          form.requestSubmit()
        }
      }
    }
  }

  // Create a reusable PromptInput component
  const renderPromptInput = () => (
            <PromptInput
              onSubmit={handleSubmit}
              accept="image/*"
              className={cn("w-full bg-card", hasMessages ? "shadow-lg" : "")}
            >
              <PromptInputBody>
        <PromptInputTextarea
          ref={textareaRef}
          placeholder={isListening ? "" : placeholder}
          disabled={isLoading || isListening}
          className="min-h-12"
          value={inputText}
          onChange={(e) => {
            setInputText(e.target.value)
            // Auto-resize on mobile for any content change
            if (isMobile() && textareaRef.current) {
              setTimeout(() => {
                if (textareaRef.current) {
                  autoResizeTextarea(textareaRef.current)
                }
              }, 0)
            }
          }}
          onKeyDown={handleKeyDown}
        />
                {/* Photo attachment preview */}
                <PromptInputAttachments>
                  {(file) => <PromptInputAttachment key={file.id} data={file} />}
                </PromptInputAttachments>

                <PromptInputToolbar className="px-3 pb-3 h-12">
                  <PromptInputTools>
                    {isListening ? (
                      // Language selector when listening
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <PromptInputButton 
                            variant="secondary" 
                            title="Select language"
                            className="bg-secondary text-secondary-foreground hover:bg-secondary/80 w-10 h-10 rounded-full p-0 !rounded-full"
                          >
                            <span className="font-medium">{getLanguageCode(selectedLanguage)}</span>
                            <span className="sr-only">Language selection</span>
                          </PromptInputButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          {availableLanguages.map((lang) => (
                            <DropdownMenuItem
                              key={lang.code}
                              onClick={() => handleLanguageChange(lang.code)}
                              className={selectedLanguage === lang.code ? 'bg-accent' : ''}
                            >
                              <span className="flex items-center gap-2">
                                <span className="font-mono text-sm">{getLanguageCode(lang.code)}</span>
                                <span>{lang.name}</span>
                              </span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </PromptInputTools>
                  <div className="flex items-center gap-1">
                    {isListening ? (
                      // Stop button when listening
                      <PromptInputButton
                        onClick={stopListening}
                        variant="destructive"
                        title="Stop listening"
                        className="animate-pulse w-10 h-10 rounded-full p-0"
                      >
                        <Square size={16} />
                        <span className="sr-only">Stop recording</span>
                      </PromptInputButton>
                    ) : (
                      <>
                        {/* Photo upload button */}
                        <PhotoUploadButton disabled={isLoading} />
                        {/* Mic button when not listening */}
                        <PromptInputButton
                          onClick={startListening}
                          variant="ghost"
                          title="Voice input"
                          disabled={isLoading}
                          className="w-10 h-10 rounded-full p-0"
                        >
                          <Mic size={16} />
                          <span className="sr-only">Start voice input</span>
                        </PromptInputButton>
                      </>
                    )}
                    <PromptInputSubmit 
                      status={getChatStatus()}
                      disabled={isLoading || isListening}
                      className="rounded-full"
                    />
                  </div>
                </PromptInputToolbar>
              </PromptInputBody>
            </PromptInput>
    )

  // New overlay layout - fixed height messages container with overlay prompt input
  return (
    <div ref={messagesContainerRef} className={cn("main-content h-full", className)}>
        
          {hasMessages ? (
            <>
              {/* Scrollable Messages Area - Only this area scrolls */}
              <div className="flex-1 w-full" style={{ paddingBottom: 'calc(8rem + env(keyboard-inset-bottom, 0px))' }}> 
                <div className="space-y-4 w-full ">
                  {messages.map((message: ChatMessage) => {
                    
                    return (
                      <Message 
                        key={message.id} 
                        from={message.role}
                        className="w-full"
                      >
                        <MessageContent variant="contained">
                          {message.role === 'assistant' ? (
                            <div className="flex items-start gap-2">
                              {message.content ? (
                                <Response>
                                  {message.content}
                                </Response>
                              ) : (isLoading || isStreaming) && message.id === messages[messages.length - 1]?.id ? (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Loader size={16} />
                                  <span>Just a sec...</span>
                                </div>
                              ) : null}
                              {isStreaming && message.content && message.id === messages[messages.length - 1]?.id && (
                                <span className="inline-flex items-center">
                                  <Loader size={12} />
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="whitespace-pre-wrap">
                              {message.content}
                            </div>
                          )}
                        </MessageContent>
                      </Message>
                    )
                  })}
                  {/* Invisible div for auto-scrolling */}
                  <div ref={messagesEndRef} />
                </div>
              </div>
              
              {/* Scroll to Bottom Button - Shows when user has scrolled up */}
              {showScrollToBottom && (
                <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 z-10" style={{ paddingBottom: 'calc(8rem + env(keyboard-inset-bottom, 0px))' }}>
                  <button
                    onClick={scrollToBottom}
                    className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-105"
                    title="Scroll to bottom"
                  >
                    <ChevronDown size={20} />
                  </button>
                </div>
              )}

              {/* Sticky Prompt Input - Sticky positioned at bottom */}
              <div className="fixed bottom-0 md:w-[784px] w-[calc(100%-1rem)] bg-background" style={{ paddingBottom: 'calc(1.5rem + env(keyboard-inset-bottom, 0px))' }} >
                {renderPromptInput()}
              </div>
            </>
          ) : (
            /* Welcome Screen - Center both welcome message and prompt input together */
            <div className="h-full flex flex-col items-center justify-center">
              <div className="text-center mb-8">
                {/* Personalized welcome */}
                <div className="flex items-center justify-center gap-3 mb-6">
                  <h2 className="text-4xl font-bold text-primary">
                    Hi, {getFirstName()}
                  </h2>
                </div>
              </div>
              
              {/* Centered Prompt Input */}
              
              <div className="w-full pb-[calc(env(keyboard-inset-bottom,0px))]">
                
                {renderPromptInput()}
              </div>
            </div>
          )}
    </div>
  )
}
